'use client';

import {Easing} from '@config/easing';
import {useUpdateEffect} from '@hooks/useUpdateEffect';
import {
    MotionProps,
    ValueAnimationTransition,
    animate,
    motion,
    useMotionValue,
} from 'framer-motion';
import {FC, ReactNode, useEffect, useRef, useState} from 'react';

interface AnimateChangeInHeightProps extends MotionProps {
    children: ReactNode;
    className?: string;
    containerClassName?: string;
    hideOverflow?: boolean;
    animateKey?: unknown; // If provided, will only animate height when animateKey updates
}

export const AnimateHeightChange: FC<AnimateChangeInHeightProps> = ({
    children,
    className,
    containerClassName,
    hideOverflow,
    animateKey,
    style,
    transition = {duration: 0.3, ease: Easing.bezier.outNormal},
    ...rest
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hasMeasuredHeight, setHasMeasuredHeight] = useState(false);
    const height = useMotionValue(0);
    const hasKeyChanged = useRef(false);

    const animateHeight = (to: number) => {
        animate(height, to, transition as ValueAnimationTransition);
    };

    const applyHeight = (to: number) => {
        height.set(to);
    };

    useUpdateEffect(() => {
        hasKeyChanged.current = true;
    }, [animateKey]);

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver(entries => {
                const observedHeight = entries[0].contentRect.height;
                setHasMeasuredHeight(true);
                if (
                    animateKey !== undefined &&
                    !hasKeyChanged.current &&
                    !height.isAnimating()
                ) {
                    applyHeight(observedHeight);
                } else {
                    animateHeight(observedHeight);
                }
                hasKeyChanged.current = false;
            });
            resizeObserver.observe(containerRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    return (
        <motion.div
            transition={transition}
            style={{
                ...style,
                ...(hideOverflow && {overflow: 'hidden'}),
                height: hasMeasuredHeight ? height : 'auto',
            }}
            className={containerClassName}
            {...rest}
        >
            <div ref={containerRef} className={className}>
                {children}
            </div>
        </motion.div>
    );
};
